import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axiosInstance";

const initialStateUsers = {
    allusers: [],
    wishlist: [],
    allAdmins: [],
    adminStats: {},
    allGuests: [],
    guestData: [],
    guestsStats: {},
    allBlockedUsers: [],
    blockStats: {},
    currUser: null,
    success: false,
    message: "",
    loading: false,
    hostKYCPending: [],
    hostKYCStats: {},
};

const handleErrors = (error, dispatch, rejectWithValue) => {
    const errorMessage = error.response?.data?.message || "An error occurred";
    return rejectWithValue(error.response?.data || { message: errorMessage });
};

export const getAllUsers = createAsyncThunk(
    "user/getAllUsers",
    async (_, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.get("/allUsers");
            return response.data.user;
        } catch (error) {
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

export const getallAdmins = createAsyncThunk(
    "user/getallAdmins",
    async (_, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.get("/getAllAdmins");
            return response.data;
        } catch (error) {
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

export const getAllBlockedUsers = createAsyncThunk(
    "user/getAllBlockedUsers",
    async (_, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.get("/getAllBlockedUsers");
            return response.data;
        } catch (error) {
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

export const getAllGuests = createAsyncThunk(
    "user/getAllGuests",
    async (_, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.get("/getAllGuests");
            return response.data;
        } catch (error) {
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

export const deleteUser = createAsyncThunk(
    "users/delete",
    async (id, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.delete(`/deleteUser/${id}`);
            return { ...response.data, id };
        } catch (error) {
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

export const updateUser = createAsyncThunk(
    "users/updateUser",
    async ({ id, values }, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.put(`/userUpdate/${id}`, values);
            return response.data;
        } catch (error) {
            return handleErrors(error, null, rejectWithValue);
        }
    }
);

export const getUserById = createAsyncThunk(
    "users/getUserById",
    async (id, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`/getUserById/${id}`);
            return response.data.user;
        } catch (error) {
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

export const getGuestById = createAsyncThunk(
    "users/getGuestById",
    async (id, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`/getGuestById/${id}`);
            return response.data;
        } catch (error) {
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

export const blockUser = createAsyncThunk(
    "user/blockUser",
    async (id, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.put(`/blockUser/${id}`);
            return response.data;
        } catch (error) {
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

export const fetchWishlist = createAsyncThunk(
    "user/fetchWishlist",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get("/getWatchlist");
            return response.data.wishlist;
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.message || "Failed to fetch watchlist"
            );
        }
    }
);

export const createWishlistCollection = createAsyncThunk(
    "user/createWishlistCollection",
    async ({ collectionName, houseId }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post("/createWishlistCollection", { collectionName, houseId });
            return response.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.message || "Failed to create wishlist collection"
            );
        }
    }
);

export const addToWishlistCollection = createAsyncThunk(
    "user/addToWishlistCollection",
    async ({ collectionName, houseId }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post("/addToWishlistCollection", { collectionName, houseId });
            return response.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.message || "Failed to add to wishlist collection"
            );
        }
    }
);

export const removeFromWishlistCollection = createAsyncThunk(
    "user/removeFromWishlistCollection",
    async ({ collectionName, houseId }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post("/removeFromWishlistCollection", { collectionName, houseId });
            return response.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.message || "Failed to remove from wishlist collection"
            );
        }
    }
);

export const deleteWishlistCollection = createAsyncThunk(
    "user/deleteWishlistCollection",
    async (collectionId, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post("/deleteWishlistCollection", { collectionId });
            return response.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.message || "Failed to delete wishlist collection"
            );
        }
    }
);


export const updateHostKYCStatus = createAsyncThunk(
    "user/updateHostKYCStatus",
    async ({ id, status, reason }, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.put(`/updateKYCStatus/${id}`, { status, reason });
            return response.data;
        } catch (error) {
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);
export const updateHouseOrHostKYCStatus = createAsyncThunk(
    "user/updateHouseOrHostKYCStatus",
    async ({ id, status, reason }, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.put(`/updateHouseOrHostKYCStatus/${id}`, { status, reason });
            return response.data;
        } catch (error) {
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

export const fetchHostKYCStats = createAsyncThunk(
    "user/fetchHostKYCStats",
    async (_, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.get("/getHostKYCStats");
            return response.data;
        } catch (error) {
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

const usersSlice = createSlice({
    name: "users",
    initialState: initialStateUsers,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // ... existing cases ...
            .addCase(getAllUsers.pending, (state) => {
                state.loading = true;
                state.message = "Fetching users...";
            })
            .addCase(getAllUsers.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.message = "Users fetched successfully";
                state.allusers = Array.isArray(action.payload) ? action.payload : [];
            })
            .addCase(getAllUsers.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.message = action.payload?.message || "Failed to fetch users";
            })
            .addCase(deleteUser.pending, (state) => {
                state.loading = true;
                state.message = "Deleting user...";
            })
            .addCase(deleteUser.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                const deletedId = action.payload.id;

                state.allusers = state.allusers.filter(
                    (user) => user._id !== deletedId
                );

                // Remove from allAdmins if present and update admin stats
                const removedFromAdmin = state.allAdmins.find(u => u._id === deletedId);
                if (removedFromAdmin) {
                    state.allAdmins = state.allAdmins.filter(
                        user => user._id !== deletedId
                    );

                    // Update Admin Stats
                    if (state.adminStats.totalUsers > 0) state.adminStats.totalUsers--;
                    if (removedFromAdmin.status === 'Active' && state.adminStats.activeUsers > 0) state.adminStats.activeUsers--;
                    if (removedFromAdmin.status === 'Block' && state.adminStats.blockedAdmins > 0) state.adminStats.blockedAdmins--;
                }

                // Remove from allGuests if present and update guest stats
                const removedFromGuests = state.allGuests.find(u => u._id === deletedId);
                if (removedFromGuests) {
                    state.allGuests = state.allGuests.filter(
                        user => user._id !== deletedId
                    );

                    // Update Guest Stats
                    if (state.guestsStats.totalUsers > 0) state.guestsStats.totalUsers--;
                    if (removedFromGuests.status === 'Active' && state.guestsStats.activeUsers > 0) state.guestsStats.activeUsers--;
                    if (removedFromGuests.status === 'Block' && state.guestsStats.blockedGuests > 0) state.guestsStats.blockedGuests--;
                }

                // Also remove from allBlockedUsers if present
                const removedFromBlocked = state.allBlockedUsers.find(u => u._id === deletedId);
                if (removedFromBlocked) {
                    state.allBlockedUsers = state.allBlockedUsers.filter(
                        user => user._id !== deletedId
                    );

                    // Optimistically update blockStats
                    if (state.blockStats.totalSuspendedUsers > 0) state.blockStats.totalSuspendedUsers--;
                    if (removedFromBlocked.role === 'admin' && state.blockStats.hostsSuspended > 0) state.blockStats.hostsSuspended--;
                    if (removedFromBlocked.role === 'user' && state.blockStats.guestsSuspended > 0) state.blockStats.guestsSuspended--;

                    // newSuspensionsToday adjustment (best effort)
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const blockedDate = new Date(removedFromBlocked.blockedAt);
                    if (!isNaN(blockedDate) && blockedDate >= today) {
                        if (state.blockStats.newSuspensionsToday > 0) state.blockStats.newSuspensionsToday--;
                    }
                }

                state.message = action.payload?.message || "User deleted successfully";
            })
            .addCase(deleteUser.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.message = action.payload?.message || "Failed to delete user";
            })

            .addCase(updateUser.pending, (state) => {
                state.loading = true;
                state.message = "Editing user...";
            })
            .addCase(updateUser.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.allusers = state.allusers.map((user) =>
                    user._id === action.payload.user._id ? action.payload.user : user
                );
                state.currUser = action.payload.user;
                state.message = action.payload?.message || "User updated successfully";
            })
            .addCase(updateUser.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.message = action.payload?.message || "Failed to update user";
            })

            .addCase(getUserById.pending, (state) => {
                state.loading = true;
                state.message = "Getting user...";
            })
            .addCase(getUserById.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.currUser = action.payload;
                state.allusers = state.allusers.map((user) =>
                    user._id === action.payload._id ? action.payload : user
                );
            })
            .addCase(getUserById.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.message = action.payload?.message || "Failed to get user";
            })

            .addCase(getGuestById.pending, (state) => {
                state.loading = true;
                state.message = "Getting user...";
            })
            .addCase(getGuestById.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.guestData = action.payload.guest;
            })
            .addCase(getGuestById.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.message = action.payload?.message || "Failed to get user";
            })

            // get wishlist
            .addCase(fetchWishlist.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchWishlist.fulfilled, (state, action) => {
                state.loading = false;
                state.wishlist = action.payload;
            })
            .addCase(fetchWishlist.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Create wishlist collection
            .addCase(createWishlistCollection.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createWishlistCollection.fulfilled, (state, action) => {
                state.loading = false;
                state.wishlist = action.payload.wishlist;
                state.message = action.payload.message;
            })
            .addCase(createWishlistCollection.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Add to wishlist collection
            .addCase(addToWishlistCollection.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(addToWishlistCollection.fulfilled, (state, action) => {
                state.loading = false;
                state.wishlist = action.payload.wishlist;
                state.message = action.payload.message;
            })
            .addCase(addToWishlistCollection.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Remove from wishlist collection
            .addCase(removeFromWishlistCollection.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(removeFromWishlistCollection.fulfilled, (state, action) => {
                state.loading = false;
                state.wishlist = action.payload.wishlist;
                state.message = action.payload.message;
            })
            .addCase(removeFromWishlistCollection.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Delete wishlist collection
            .addCase(deleteWishlistCollection.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteWishlistCollection.fulfilled, (state, action) => {
                state.loading = false;
                state.wishlist = action.payload.wishlist;
                state.message = action.payload.message;
            })
            .addCase(deleteWishlistCollection.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(getallAdmins.pending, (state) => {
                state.loading = true;
                state.message = "Fetching users...";
            })
            .addCase(getallAdmins.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.message = "Users fetched successfully";
                state.allAdmins = Array.isArray(action.payload.admin) ? action.payload.admin : [];
                state.adminStats = typeof action.payload.stats === 'object' && action.payload.stats !== null ? action.payload.stats : {};
            })
            .addCase(getallAdmins.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.message = action.payload?.message || "Failed to fetch users";
            })
            .addCase(getAllGuests.pending, (state) => {
                state.loading = true;
                state.message = "Fetching users...";
            })
            .addCase(getAllGuests.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.message = "Users fetched successfully";
                state.allGuests = Array.isArray(action.payload.guests) ? action.payload.guests : [];
                state.guestsStats = typeof action.payload.stats === 'object' && action.payload.stats !== null ? action.payload.stats : {};
            })
            .addCase(getAllGuests.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.message = action.payload?.message || "Failed to fetch users";
            })
            // block user
            .addCase(blockUser.pending, (state) => {
                state.loading = true;
                state.message = "Updating user status...";
            })
            .addCase(blockUser.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.message = action.payload.message;
                const updatedUser = action.payload.user;

                // Update in allGuests
                state.allGuests = state.allGuests.map(user =>
                    user._id === updatedUser._id ? updatedUser : user
                );

                // Update in allAdmins
                state.allAdmins = state.allAdmins.map(user =>
                    user._id === updatedUser._id ? updatedUser : user
                );

                // Update in allusers
                state.allusers = state.allusers.map(user =>
                    user._id === updatedUser._id ? updatedUser : user
                );

                // Update specific user if loaded (currUser)
                if (state.currUser && state.currUser._id === updatedUser._id) {
                    state.currUser = updatedUser;
                }

                // Update guestData if loaded (for GuestsDetails page)
                if (state.guestData && state.guestData._id === updatedUser._id) {
                    state.guestData = { ...state.guestData, status: updatedUser.status };
                }

                // Update allBlockedUsers list and stats
                if (updatedUser.status === 'Active') {
                    // User was unblocked - Remove form blocked list
                    const wasInBlocked = state.allBlockedUsers.find(u => u._id === updatedUser._id);
                    if (wasInBlocked) {
                        state.allBlockedUsers = state.allBlockedUsers.filter(
                            user => user._id !== updatedUser._id
                        );

                        // Update Stats (decrement)
                        if (state.blockStats.totalSuspendedUsers > 0) state.blockStats.totalSuspendedUsers--;
                        if (updatedUser.role === 'admin' && state.blockStats.hostsSuspended > 0) state.blockStats.hostsSuspended--;
                        if (updatedUser.role === 'user' && state.blockStats.guestsSuspended > 0) state.blockStats.guestsSuspended--;

                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const blockedDate = new Date(wasInBlocked.blockedAt);
                        if (!isNaN(blockedDate) && blockedDate >= today) {
                            if (state.blockStats.newSuspensionsToday > 0) state.blockStats.newSuspensionsToday--;
                        }
                    }
                } else if (updatedUser.status === 'Block') {
                    // User was blocked - Add/Update in blocked list
                    const exists = state.allBlockedUsers.find(u => u._id === updatedUser._id);
                    if (!exists) {
                        state.allBlockedUsers.push(updatedUser);

                        // Update Stats (increment)
                        state.blockStats.totalSuspendedUsers = (state.blockStats.totalSuspendedUsers || 0) + 1;
                        if (updatedUser.role === 'admin') state.blockStats.hostsSuspended = (state.blockStats.hostsSuspended || 0) + 1;
                        if (updatedUser.role === 'user') state.blockStats.guestsSuspended = (state.blockStats.guestsSuspended || 0) + 1;
                        state.blockStats.newSuspensionsToday = (state.blockStats.newSuspensionsToday || 0) + 1;
                    } else {
                        state.allBlockedUsers = state.allBlockedUsers.map(user =>
                            user._id === updatedUser._id ? updatedUser : user
                        );
                    }
                }
            })
            .addCase(blockUser.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.message = action.payload?.message || "Failed to update user status";
            })
            .addCase(getAllBlockedUsers.pending, (state) => {
                state.loading = true;
                state.message = "Fetching users...";
            })
            .addCase(getAllBlockedUsers.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.message = "Users fetched successfully";
                state.allBlockedUsers = Array.isArray(action.payload.users) ? action.payload.users : [];
                state.blockStats = typeof action.payload.stats === 'object' && action.payload.stats !== null ? action.payload.stats : {};
            })
            .addCase(getAllBlockedUsers.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.message = action.payload?.message || "Failed to fetch users";
            })
            // Host KYC
            .addCase(updateHostKYCStatus.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.message = action.payload.message;
                state.hostKYCStats = action.payload.stats;
                state.hostKYCPending = action.payload.hostKYCPending;
                state.currUser = action.payload.user;
            })
            .addCase(updateHostKYCStatus.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.message = action.payload?.message || "Failed to update user status";
            })
            .addCase(updateHostKYCStatus.pending, (state) => {
                state.loading = true;
                state.message = "Updating user status...";
            })
            // Host  or house KYC
            .addCase(updateHouseOrHostKYCStatus.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.message = action.payload.message;
                state.hostKYCStats = action.payload.stats;
                state.hostKYCPending = action.payload.hostKYCPending;
                state.currUser = action.payload.user;
            })
            .addCase(updateHouseOrHostKYCStatus.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.message = action.payload?.message || "Failed to update user status";
            })
            .addCase(updateHouseOrHostKYCStatus.pending, (state) => {
                state.loading = true;
                state.message = "Updating user status...";
            })
            .addCase(fetchHostKYCStats.pending, (state) => {
                state.loading = true;
                state.message = "Fetching users...";
            })
            .addCase(fetchHostKYCStats.fulfilled, (state, action) => {
                state.hostKYCStats = action.payload.stats;
                state.hostKYCPending = action.payload.hostKYCPending;
                state.loading = false;
                state.success = true;
                state.message = action.payload.message;
            })
            .addCase(fetchHostKYCStats.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.message = action.payload?.message || "Failed to fetch users";
            })
    },
});

export default usersSlice.reducer;